import { useContext } from 'react'

import {
  Button,
  DashboardModule,
  Form,
  LoadingModule, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import usePriorityCampaign from '@hooks/usePriorityCampaign'
import usePriorityCampaigns from '@hooks/usePriorityCampaigns'
import useUser from '@hooks/useUser'

import EntitySelector from '@components/EntitySelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import PriorityCampaignListItem from './components/PriorityCampaignListItem'

const MODAL_KEY = 'ManageUserPriorityCampaignsModal'

type ModalCallbacks = {
  closeModal: () => void,
  createPriorityCampaign: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageUserPriorityCampaignsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createPriorityCampaign } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { priorityCampaign: initPriorityCampaign } = modalPayload

  const {
    priorityCampaign: {
      owner_id: userId,
    },
    callbacks: {
      createPriorityCampaign: createFn,
    },
  } = usePriorityCampaign(initPriorityCampaign)

  const {
    image,
    user: {
      email, full_name,
    },
  } = useUser({ id: userId })

  const priorityCampaignsPayload = usePriorityCampaigns({
    filters: {
      owner_id: userId,
      owner_type: 'User',
    },
    performHttpRequests: !!userId,
    requestOptions: {
      include_campaign_image: true,
      include_image_sizes: true,
      include_priority_campaign_campaign: true,
      include_priority_campaign_owner: true,
      include_user_image: true,
      use_campaign_first_image: true,
    },
  })

  const {
    filteredPriorityCampaigns,
    hasFilteredPriorityCampaigns,
    loading: loadingPriorityCampaigns,
  } = priorityCampaignsPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Priority" titleSecondLine="Settings" />

      <SidebarModal.Content>
        <LoadingModule loading={loadingPriorityCampaigns} />

        {!loadingPriorityCampaigns && (
          <Form>
            <Form.Field label="Managing Priority Campaigns For:" labelProps={{ marginBottom: 'none' }}>
              <SidebarModal.User
                disableHover
                // @ts-expect-error TS2339
                imageUrl={image?.thumb_url || null}
                subtitle={email || 'No Email'}
                title={full_name}
              />
            </Form.Field>

            {/* @ts-expect-error */}
            <EntitySelector
              callbacks={{
                selectItem: item => createPriorityCampaign({
                  callbacks: { action: createFn },
                  entityParams: { ...initPriorityCampaign, campaign_id: item?.id },
                  toastText: 'Campaign Added',
                }),
              }}
              entityKey="campaigns"
              label="Add a Campaign"
              marginTop="medium"
              requestOptions={{
                include_campaign_image: true,
                use_campaign_first_image: true,
              }}
              showSelectedEntity={false}
              searchRequestOptions={{ hidden: false }}
              selectedIds={filteredPriorityCampaigns.map(
                filteredPriorityCampaign => filteredPriorityCampaign.campaign_id,
              )}
            />

            {hasFilteredPriorityCampaigns && (
              <>
                <SectionDivider marginTop="large">Selected Campaigns</SectionDivider>

                {/*
                 // @ts-expect-error TS2322 */}
                <DashboardModule.ScrollableSection maxHeight={600} flexDirection="column">
                  {filteredPriorityCampaigns.map(priorityCampaign => (
                    <PriorityCampaignListItem
                      key={priorityCampaign.id}
                      priorityCampaign={priorityCampaign}
                    />
                  ))}
                </DashboardModule.ScrollableSection>
              </>
            )}
          </Form>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close Modal
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageUserPriorityCampaignsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
